import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import useAuthStore from '../../../../stores/authStore';
import VERSION_CONFIG from '../../../../config/version';
import './Login.css';

/**
 * Restrict post-login redirect to site-internal absolute paths.
 * Blocks `http://...`, `//evil.com`, and other open-redirect attempts.
 */
const isSafeNextPath = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  return true;
};

const Login = () => {
  const [searchParams] = useSearchParams();
  const nextRaw = searchParams.get('next');
  const redirectTo = nextRaw && isSafeNextPath(nextRaw) ? nextRaw : '/chat';

  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [wecomProcessing, setWecomProcessing] = useState(false);
  const wecomCallbackHandled = useRef(false);

  const {
    login,
    loginWithWeCom,
    exchangeWeComCode,
    register,
    isAuthenticated,
    isInitialized,
    isLoading: authLoading,
  } = useAuthStore();

  // The backend callback redirects here with a one-time code. Exchange it
  // once, then remove credentials from the address bar after React redirects.
  useEffect(() => {
    const code = searchParams.get('wecom_code');
    const state = searchParams.get('state');
    const callbackError = searchParams.get('wecom_error');

    if (callbackError) {
      setError('企业微信授权失败，请重试。');
      return;
    }
    if (!code || !state || wecomCallbackHandled.current) return;

    wecomCallbackHandled.current = true;
    setWecomProcessing(true);
    exchangeWeComCode(code, state).then((result) => {
      if (!result.success) setError(result.message || '企业微信登录失败');
      setWecomProcessing(false);
    });
  }, [exchangeWeComCode, searchParams]);

  // Show loading while auth is initializing - prevent flicker
  if (!isInitialized || wecomProcessing) {
    return (
      <div className="auth-login-container">
        <div className="auth-login-card" style={{ textAlign: 'center' }}>
          <div className="auth-loading-spinner" style={{ justifyContent: 'center' }}>
            <div className="auth-spinner"></div>
            <span>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Redirect if already authenticated - use Navigate to avoid flash
  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      let result;

      if (mode === 'login') {
        result = await login(username, password);
      } else {
        // Registration validation
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setIsLoading(false);
          return;
        }

        if (password.length < 8) {
          setError('Password must be at least 8 characters long');
          setIsLoading(false);
          return;
        }

        result = await register(username, email, password);

        if (result.success) {
          setSuccess('Registration successful! Please log in with your credentials.');
          setMode('login');
          setEmail('');
          setPassword('');
          setConfirmPassword('');
        }
      }

      if (!result.success) {
        setError(result.message || 'An error occurred');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError('');
    setSuccess('');
    setUsername('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="auth-login-container">
      <div className="auth-login-card">
        <div className="auth-login-header">
          <div className="auth-login-logo">
            <svg width="60" height="60" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(30,40)">
                <rect x="0" y="10" width="12" height="50" fill="currentColor" rx="2"/>
                <rect x="18" y="20" width="12" height="40" fill="currentColor" opacity="0.8" rx="2"/>
                <rect x="36" y="0" width="12" height="60" fill="currentColor" opacity="0.6" rx="2"/>
              </g>
            </svg>
          </div>
          <h1 className="auth-login-title">Data Intelligence</h1>
          <p className="auth-login-subtitle">
            {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-login-form">
          {error && (
            <div className="auth-error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="auth-success-message">
              {success}
            </div>
          )}

          <div className="auth-form-group">
            <label htmlFor="username" className="auth-form-label">Username</label>
            <input
              type="text"
              id="username"
              data-testid="username-input"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="auth-form-input"
              placeholder="Enter your username"
              required
              disabled={isLoading || authLoading}
              autoComplete="username"
              spellCheck="false"
            />
          </div>

          {mode === 'register' && (
            <div className="auth-form-group">
              <label htmlFor="email" className="auth-form-label">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-form-input"
                placeholder="Enter your email"
                required
                disabled={isLoading || authLoading}
              />
            </div>
          )}

          <div className="auth-form-group">
            <label htmlFor="password" className="auth-form-label">Password</label>
            <input
              type="password"
              id="password"
              data-testid="password-input"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-form-input"
              placeholder={mode === 'register' ? 'At least 8 characters' : 'Enter your password'}
              required
              disabled={isLoading || authLoading}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              spellCheck="false"
            />
          </div>

          {mode === 'register' && (
            <div className="auth-form-group">
              <label htmlFor="confirmPassword" className="auth-form-label">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="auth-form-input"
                placeholder="Confirm your password"
                required
                disabled={isLoading || authLoading}
              />
            </div>
          )}

          <button
            type="submit"
            data-testid="login-button"
            className="auth-login-button"
            disabled={isLoading || authLoading}
          >
            {(isLoading || authLoading) ? (
              <div className="auth-loading-spinner">
                <div className="auth-spinner"></div>
                <span>{mode === 'login' ? 'Signing in...' : 'Creating account...'}</span>
              </div>
            ) : (
              mode === 'login' ? 'Sign In' : 'Create Account'
            )}
          </button>

          {mode === 'login' && (
            <>
              <div className="auth-login-method-divider" aria-hidden="true">
                <span>或</span>
              </div>
              <div className="auth-wecom-login-row">
                <button
                  type="button"
                  className="auth-wecom-button"
                  aria-label="使用企业微信登录"
                  title="使用企业微信登录"
                  onClick={() => {
                    setError('');
                    setSuccess('');
                    const inWeComApp = /wxwork/i.test(window.navigator.userAgent);
                    loginWithWeCom(inWeComApp ? 'app' : 'qr');
                  }}
                  disabled={isLoading || authLoading}
                >
                  <img
                    className="auth-wecom-icon"
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAADNElEQVR4Ac1XU7jlSBistXdf1rbH1uPYtm3bk4xt27Zt27aR5JzxQU9d9+05fa2H+pLu/tNVPxpB3maFkhXRGxjuf2A6ndDDWgLDusLnE/SwPTDsB3zuYd8IvpfCPPFuggqA4RQgwS4SipggVFBfDBOfxksAej7+ipMtVghiAfs2TLtknASgl+tPkl8KMOldhnoqTKcJn8VCouNUAj1me2dAIabdIVYCSPwLJ7yvhPUc6A3GineinKS38zvtxtPerwjpHiMBzNt79ORoZHJrSGwLC6YrO0XckRzws69otAJo2D4yudM5ttWsRPK2NN91RvBDrT04SG9dUr43QYg34rW2DVchJZrN9QJMu6xk/AR9nv2YIBuM6UyWnDqgtePgAilns+TBeEbhr0hR6G39HFiAYR2PWDpO+QTdZg3rhFRX+XQC7HCjXq5sCSrAtFowqjWJcTBdVXUpsCIi4MqeaIcOC1snYJ+Uq2oJSZpqA3qk2ggvny9Sb0Y5XQpmSEU4LiEFpN4QTC6CsRGPNALsutJyeYb+7i8TTMBGeCQB/vS78PvrAgaJDyjiXmJEIc1GtCO5LGKxZit2OisnWZv4EItDmCkO4HLwu8AbJG9I8ht8+v/fijyBDqNPwZNPPkSI2rEmPoD6xHXCL/YjvzrOQpzNtJzSHMfuf5QzwYPN4u0YER9ER5J6CUEIki/T2aK7NYBz+8jxnPtOMfUkGyffBUJuSE5BCpvOPgM8WtHTSote7n/5cY7gCwpriKSecPKD8PP5d0By2oaSixA8ui0byEuSsNewvTSae+BmfgfZe+Khfme02ynH/k1FQKzvgitCBXQPFfFCrERTXz2MDySA9lcl8U/Q80l+VcB6zUXzPnGa414lAl61TkQzfOOphRfeOrgoquOHiMuu3S+0uPejW8TBFHUEDPslMQB9xGfB47wbMv9foO+TbzFSfKy76Yh6qOitDX8orotaaEIH3DCspqqternMFLIp2T4+F8Jw/ojrXsAI3PbUhgiDqIt6MfsvmCfeisXPhRb0fJwsgIKW6QQkMKRaqA0vISjGL2ojf6ILUCEa4W8W5FDRGLl0Nin97zjx8Qo+a7Io2itFNwAAAABJRU5ErkJggg=="
                    alt="企业微信"
                    aria-hidden="true"
                  />
                  <span className="auth-visually-hidden">使用企业微信登录</span>
                </button>
              </div>
            </>
          )}

          {mode === 'register' && (
            <div className="auth-switch-section">
              <p>
                Already have an account?{' '}
                <button
                  type="button"
                  className="auth-switch-mode-button"
                  onClick={switchMode}
                  disabled={isLoading || authLoading}
                >
                  Sign In
                </button>
              </p>
            </div>
          )}
        </form>

        <div className="auth-login-footer">
          <div className="auth-login-footer-text">
            © 2026 Datacenter
          </div>
          <div className="auth-login-version">
            v{VERSION_CONFIG.FRONTEND_VERSION}
          </div>
          {mode === 'login' && (
            <div className="auth-account-help">
              <p className="auth-account-help-text">
                没有 Data Intelligence 账号？请联系数据中心开通
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
